import { logException } from '@/logging/sentry'
import {
  ChargeSchedule,
  ChargeScheduleDaysOfWeek,
  DayOfWeek,
  MissingDayOfWeekError,
} from '@/types/chargeSchedule'
import { groupBy } from '@/utils/array/groupBy'
import { convertTimeTo12HourFormat } from '@/utils/date'
import { dayOfWeekIndex } from '@/utils/date/dayOfWeek'
import daysOfWeekToReadable from '@/utils/date/daysOfWeekToReadable'

function getScheduleDayOfWeekWithFirstDay(
  scheduleDays: ChargeScheduleDaysOfWeek[],
): ChargeScheduleDaysOfWeek {
  scheduleDays.sort((a, b) => {
    if (!a.day_of_week || !b.day_of_week) {
      return 0
    }
    return dayOfWeekIndex(a.day_of_week) - dayOfWeekIndex(b.day_of_week)
  })
  return scheduleDays[0]
}

function createTimeDescriptions(
  daysOfWeek: ChargeScheduleDaysOfWeek[],
  timeField: keyof ChargeScheduleDaysOfWeek,
  prefix: string,
): string[] {
  const timeGrouped = groupBy(daysOfWeek, timeField)
  return (
    Object.entries(timeGrouped)
      // groups of schedule days with the same time are shown together. We want to
      // show the ones with the first occuring day of the week first
      .toSorted((a, b) => {
        const [, scheduleDaysA] = a
        const [, scheduleDaysB] = b
        const scheduleDayA = getScheduleDayOfWeekWithFirstDay(scheduleDaysA)
        const scheduleDayB = getScheduleDayOfWeekWithFirstDay(scheduleDaysB)
        if (!scheduleDayA.day_of_week || !scheduleDayB.day_of_week) {
          logException(
            new MissingDayOfWeekError(
              'Missing day of week when trying to sort charge schedule days of week',
            ),
          )
        }
        return (
          dayOfWeekIndex(scheduleDayA.day_of_week ?? 'saturday') -
          dayOfWeekIndex(scheduleDayB.day_of_week ?? 'saturday')
        )
      })
      .filter(([time]) => time !== null)
      .map(([time, days]) => {
        const formattedTime = convertTimeTo12HourFormat(time)
        // with a group of schedule days that all have the same time, we want to
        // show the first occuring day of the week first
        const sortedDays = days
          .toSorted((a, b) => {
            if (!a.day_of_week || !b.day_of_week) {
              logException(
                new MissingDayOfWeekError(
                  'Missing day of week when trying to sort charge schedule days of week',
                ),
              )
              return 0
            }
            return dayOfWeekIndex(a.day_of_week) - dayOfWeekIndex(b.day_of_week)
          })
          .filter((day) => Boolean(day.day_of_week))
          .map((day) => day.day_of_week as DayOfWeek)
        const readableDays = daysOfWeekToReadable(sortedDays, 'short')
        return `${prefix}: ${formattedTime} on ${readableDays}`
      })
  )
}

export function daysOfWeekToScheduleDescription(
  daysOfWeek: ChargeScheduleDaysOfWeek[],
): string[] {
  // group by departure time and start time,
  // then convert to a string that looks like "Ready by: 8:00 AM on weekdays" using daysOfWeekToReadable
  const departureDescriptions = createTimeDescriptions(
    daysOfWeek,
    'departure_time',
    'Ready by',
  )
  const startDescriptions = createTimeDescriptions(
    daysOfWeek,
    'charging_start_time',
    'Start charge',
  )
  return [...departureDescriptions, ...startDescriptions]
}

export function scheduleToGoalsDescriptions(chargeSchedule?: ChargeSchedule) {
  const readableMapping = {
    save_the_planet_plan_enabled: 'Save the Planet',
    money_saving_plan_enabled: 'Save Money',
    solar_surplus_plan_enabled: 'Solar Surplus',
  } as Record<keyof ChargeSchedule, string>

  const enabledPlans: (keyof ChargeSchedule)[] = []
  const disabledPlans: (keyof ChargeSchedule)[] = []
  if (!chargeSchedule) {
    return {
      enabledPlans: [],
      disabledPlans: [],
    }
  }

  const planSettings = [
    'save_the_planet_plan_enabled',
    'money_saving_plan_enabled',
    'solar_surplus_plan_enabled',
  ] as (keyof ChargeSchedule)[]

  planSettings.forEach((plan) => {
    if (chargeSchedule[plan]) {
      enabledPlans.push(plan)
    } else {
      disabledPlans.push(plan)
    }
  })

  return {
    enabledPlans: enabledPlans.map((plan) => readableMapping[plan]),
    disabledPlans: disabledPlans.map((plan) => readableMapping[plan]),
  }
}
